/**
 * <sticky-atc> — the buy bar that appears once the real one has scrolled away.
 *
 * It mirrors rather than reimplements. The price is copied from whatever the
 * price block rendered (so the sale badge, the currency format and the variant
 * updates all come along for free), the label and the disabled state are copied
 * from the real button, and a click is forwarded to the real form. That leaves
 * exactly one add-to-cart implementation on the page.
 *
 * It shows only when the real button has gone *upwards* off screen. Appearing
 * while the buy button is still below the fold — on the way down to it — would
 * put two identical buttons on screen at once.
 */
(() => {
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-sticky-panel]');
      this.priceHost = this.querySelector('[data-sticky-price]');
      this.button = this.querySelector('[data-sticky-add]');

      this.form = document.querySelector('[data-product-form]');
      this.realButton = document.querySelector('[data-add-button]');
      this.realPrice = document.querySelector('[data-product-price]');

      // Without a form and a button to shadow there is nothing to mirror.
      if (!this.panel || !this.form || !this.realButton) {
        this.remove();
        return;
      }

      this.#syncPrice();
      this.#syncButton();

      this.button.addEventListener('click', () => {
        // requestSubmit runs the form's own submit handling, unlike submit().
        this.form.requestSubmit();
      });

      // The price block is re-rendered wholesale on a variant change, so watch
      // its children rather than any one node inside it.
      if (this.realPrice) {
        this.priceObserver = new MutationObserver(() => this.#syncPrice());
        this.priceObserver.observe(this.realPrice, { childList: true, subtree: true, characterData: true });
      }

      this.buttonObserver = new MutationObserver(() => this.#syncButton());
      this.buttonObserver.observe(this.realButton, {
        childList: true,
        characterData: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled'],
      });

      this.visibility = new IntersectionObserver(
        ([entry]) => {
          this.scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.#toggle(this.scrolledPast && !this.footerVisible);
        },
        { threshold: 0 }
      );
      const footer = document.querySelector('.footer');
      if (footer) {
        const box = footer.getBoundingClientRect();
        this.footerVisible = box.top < window.innerHeight && box.bottom > 0;
        this.footerObserver = new IntersectionObserver(([entry]) => {
          this.footerVisible = entry.isIntersecting;
          this.#toggle(this.scrolledPast && !this.footerVisible);
        }, { threshold: 0 });
        this.footerObserver.observe(footer);
      }
      this.visibility.observe(this.realButton);
    }

    disconnectedCallback() {
      this.visibility?.disconnect();
      this.priceObserver?.disconnect();
      this.buttonObserver?.disconnect();
      this.footerObserver?.disconnect();
      clearTimeout(this.hideTimer);
    }

    #toggle(show) {
      if (show === this.shown) return;
      this.shown = show;
      clearTimeout(this.hideTimer);

      if (show) {
        this.panel.hidden = false;
        // Committing the start state synchronously; a rAF here would not run in a
        // throttled tab and the bar would sit off screen with nothing to show it.
        void this.panel.offsetHeight;
        this.panel.classList.add('is-visible');
      } else {
        this.panel.classList.remove('is-visible');
        this.hideTimer = setTimeout(() => {
          this.panel.hidden = true;
        }, 300);
      }

      // The chat launcher sits in the same corner; the stylesheet tucks it away
      // while this class is present.
      this.classList.toggle('is-visible', show);
    }

    #syncPrice() {
      if (!this.realPrice || !this.priceHost) return;
      // The element itself is cloned, not just its children: the classes on it
      // carry the heading typeface and the size step, and bare spans would fall
      // back to body text. Its hooks are stripped so the page keeps exactly one
      // element answering to [data-product-price].
      const clone = this.realPrice.cloneNode(true);
      clone.removeAttribute('data-product-price');
      clone.removeAttribute('data-save-format');
      clone.removeAttribute('data-save-label');
      this.priceHost.replaceChildren(clone);
    }

    #syncButton() {
      this.button.textContent = this.realButton.textContent.trim();
      this.button.disabled = this.realButton.disabled;
    }
  }

  if (!customElements.get('sticky-atc')) {
    customElements.define('sticky-atc', StickyAtc);
  }
})();
